import React, { useEffect, useState } from 'react';
import { adminUsers, adminStats, adminUserUpdate, adminUserDelete } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const UsuariosSection = () => {
  const { user: eu } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([adminUsers(), adminStats()])
      .then(([u, s]) => { setUsers(u); setStats(s); })
      .finally(() => setLoading(false));
  }, []);

  const alternar = async (u) => {
    setErr(null);
    const novo = !u.pre_postagem_direta;
    try {
      await adminUserUpdate(u.id, { pre_postagem_direta: novo });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, pre_postagem_direta: novo } : x)));
    } catch { setErr('Não foi possível alterar a permissão.'); }
  };

  const excluir = async (u) => {
    setErr(null);
    if (!window.confirm(`Excluir a conta de ${u.name}? O acesso e os clientes salvos dela serão apagados. O histórico de envios e pagamentos é mantido.`)) return;
    try {
      await adminUserDelete(u.id, false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof d === 'string' && d.includes('saldo')) {
        if (!window.confirm(`${d}`)) return;
        try { await adminUserDelete(u.id, true); } catch (e2) { setErr(e2?.response?.data?.detail || 'Não foi possível excluir.'); return; }
      } else { setErr(typeof d === 'string' ? d : 'Não foi possível excluir.'); return; }
    }
    setUsers((list) => list.filter((x) => x.id !== u.id));
    adminStats().then(setStats).catch(() => {});
  };

  const alternarAdmin = async (u) => {
    setErr(null);
    const novo = !u.is_admin;
    if (novo && !window.confirm(`Dar acesso de administrador a ${u.name}? Essa pessoa poderá ver usuários, saldos e configurações.`)) return;
    try {
      await adminUserUpdate(u.id, { is_admin: novo });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, is_admin: novo } : x)));
    } catch (e) { setErr(e?.response?.data?.detail || 'Não foi possível alterar o acesso.'); }
  };

  const salvarMargem = async (u, txt) => {
    setErr(null);
    const limpo = txt.trim().replace(',', '.');
    const valor = limpo === '' ? null : Number(limpo);
    if (valor !== null && (Number.isNaN(valor) || valor < 0 || valor > 40)) { setErr('A margem do cliente deve ficar entre 0 e 40.'); return; }
    if ((u.margem_percent ?? null) === valor) return;
    try {
      await adminUserUpdate(u.id, { margem_percent: valor });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, margem_percent: valor } : x)));
    } catch { setErr('Não foi possível salvar a margem.'); }
  };

  return (
    <section>
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-5"><p className="text-sm text-slate-500">Usuários</p><p className="text-2xl font-bold">{stats.usuarios}</p></div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5"><p className="text-sm text-slate-500">Envios</p><p className="text-2xl font-bold">{stats.envios}</p></div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5"><p className="text-sm text-slate-500">Saldo em carteiras</p><p className="text-2xl font-bold">{brl(stats.saldo_total)}</p></div>
        </div>
      )}
      <p className="text-sm text-slate-500 mb-3">
        <strong>Cobrança direta:</strong> o usuário cria envios sem debitar a carteira e sem precisar de saldo; o custo de cada envio fica no seu contrato dos Correios (você paga na fatura). Acompanhe o valor no Painel. <strong>Margem:</strong> deixe em branco para usar a margem padrão; preencha para dar uma margem própria ao cliente.
      </p>
      {err && <p className="text-sm text-red-600 mb-2">{err}</p>}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b border-slate-100">
            <tr><th className="p-4">Nome</th><th className="p-4">E-mail</th><th className="p-4">Saldo</th><th className="p-4">Cadastro</th><th className="p-4">Administrador</th><th className="p-4">Margem (%)</th><th className="p-4" title="Envios sem débito na carteira; o custo fica no seu contrato dos Correios">Cobrança direta</th><th className="p-4">Excluir</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td className="p-4" colSpan={8}>Carregando...</td></tr>}
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-50">
                <td className="p-4 font-medium">{u.name}</td>
                <td className="p-4">{u.email}</td>
                <td className="p-4">{brl(u.saldo)}</td>
                <td className="p-4">{u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                <td className="p-4">
                  {u.is_super_admin ? (
                    <span className="text-xs font-semibold text-[#2A5BC7]">Dono</span>
                  ) : (
                    <button
                      type="button" role="switch" aria-checked={!!u.is_admin} disabled={!eu?.is_super_admin} onClick={() => alternarAdmin(u)}
                      title={eu?.is_super_admin ? '' : 'Só o dono da conta pode alterar'}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 ${u.is_admin ? 'bg-[#F77820]' : 'bg-slate-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${u.is_admin ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  )}
                </td>
                <td className="p-4">
                  <input
                    defaultValue={u.margem_percent ?? ''} placeholder="padrão" inputMode="decimal"
                    onBlur={(e) => salvarMargem(u, e.target.value)}
                    className="w-20 h-8 rounded-md border border-slate-200 px-2 text-sm"
                  />
                </td>
                <td className="p-4">
                  <button
                    type="button" role="switch" aria-checked={!!u.pre_postagem_direta} onClick={() => alternar(u)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${u.pre_postagem_direta ? 'bg-[#2A5BC7]' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${u.pre_postagem_direta ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </td>
                <td className="p-4">
                  {eu?.is_super_admin && !u.is_super_admin && u.id !== eu?.id ? (
                    <button type="button" onClick={() => excluir(u)} className="text-red-500 hover:text-red-700 text-xs font-semibold" aria-label={`Excluir ${u.name}`}>Excluir</button>
                  ) : <span className="text-slate-300">-</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default UsuariosSection;
